/**
 * Case Transformation Utilities
 *
 * Utilities for transforming between snake_case (API) and camelCase (frontend)
 * for meal plan editing operations.
 *
 * @module utils/caseTransform
 */

/**
 * Transform object keys from snake_case to camelCase recursively
 */
export function toCamelCase<T = unknown>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCase(item)) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const result: Record<string, unknown> = {};
    for (const key in obj as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        result[camelKey] = toCamelCase((obj as Record<string, unknown>)[key]);
      }
    }
    return result as T;
  }

  return obj as T;
}

/**
 * Transform object keys from camelCase to snake_case recursively
 */
export function toSnakeCase<T = unknown>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item)) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const result: Record<string, unknown> = {};
    for (const key in obj as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        result[snakeKey] = toSnakeCase((obj as Record<string, unknown>)[key]);
      }
    }
    return result as T;
  }

  return obj as T;
}